import { Button, Card } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Bot } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { McpScopePicker } from '../components/settings/McpScopePicker.tsx'
import { ApiError, meQuery } from '../lib/api.ts'
import { mcpConsentQuery, rawOAuthQuery, type ScopeChoice, submitConsent } from '../lib/mcp.ts'

/**
 * MCP consent (§13.1): Better Auth redirects here with a signed query (client_id, scope, …). The user picks
 * the containers and read/write; the api stores the grant and answers the OAuth consent (ADR 0009).
 */
export const Route = createFileRoute('/oauth/consent')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } })
  },
  component: Consent,
})

function Consent() {
  const { t } = useTranslation()
  const [oauthQuery] = useState(() => rawOAuthQuery('/oauth/consent'))
  const info = useQuery(mcpConsentQuery(oauthQuery))
  const [choice, setChoice] = useState<ScopeChoice>({
    containerMode: 'default',
    containerIds: [],
    canWrite: true,
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // reconnecting a known client: start from its current grant
  useEffect(() => {
    const g = info.data?.grant
    if (g) setChoice({ containerMode: g.containerMode, containerIds: g.containerIds, canWrite: g.canWrite })
    else if (info.data && !info.data.scopes.includes('brain:write'))
      setChoice((c) => ({ ...c, canWrite: false }))
  }, [info.data])

  async function decide(accept: boolean) {
    setBusy(true)
    setError(null)
    try {
      const { url } = await submitConsent(oauthQuery, accept, choice)
      window.location.assign(url)
    } catch (e) {
      setBusy(false)
      setError(e instanceof ApiError ? e.message : t('common.error'))
    }
  }

  const selectedNone = choice.containerMode === 'selected' && choice.containerIds.length === 0
  return (
    <div className="flex min-h-dvh items-center justify-center bg-page px-4 py-10">
      <Card className="w-full max-w-lg p-8">
        <div className="mb-6 flex size-11 items-center justify-center rounded-tile bg-ink text-white">
          <Bot size={22} strokeWidth={1.9} />
        </div>
        {info.isPending && <p className="text-[14px] text-meta">{t('common.loading')}</p>}
        {info.isError && (
          <p role="alert" className="text-[14px] text-apricot-fg">
            {info.error instanceof ApiError ? info.error.message : t('common.error')}
          </p>
        )}
        {info.data && (
          <>
            <h1 className="text-[22px] text-text-1">
              {t('mcp.consent.title', { client: info.data.clientName })}
            </h1>
            <p className="mt-1 text-[14px] text-text-2">{t('mcp.consent.subtitle')}</p>
            {info.data.clientUri && (
              <p className="mt-1 truncate text-[12px] text-meta">{info.data.clientUri}</p>
            )}
            <div className="mt-6">
              <McpScopePicker value={choice} onChange={setChoice} />
            </div>
            <p className="mt-6 text-[12px] text-meta leading-relaxed">{t('mcp.consent.manage')}</p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => decide(false)} disabled={busy}>
                {t('mcp.consent.deny')}
              </Button>
              <Button variant="primary" onClick={() => decide(true)} disabled={busy || selectedNone}>
                {t('mcp.consent.allow')}
              </Button>
            </div>
          </>
        )}
        <div aria-live="polite" className="mt-3 min-h-5 text-[13px]">
          {error && (
            <p role="alert" className="text-apricot-fg">
              {error}
            </p>
          )}
        </div>
      </Card>
    </div>
  )
}
